import type { Handler } from './handler';
import type {
  ApiFetchBannersPort,
  Banner,
} from './ports/api-fetch-banners.port';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';
import bannersFallback from '~/assets/json/banners.fallback.json';

const CACHE_TTL_MS = 5 * 60 * 1000;

type BannersResult = { mainBanners: Banner[]; homeBanners: Banner[] };

let cached: BannersResult | null = null;
let cachedAt = 0;
let inflight: Promise<BannersResult | null> | null = null;

const mapRawBanner = (
  type: 'HOME_BANNER' | 'MAIN_BANNER',
  item: any,
): Banner => ({
  id: item.id,
  filename: item.filename,
  url: item.url,
  link_url: item.link_url || null,
  button_text: item.button_text || null,
  type,
  created_at: item.created_at,
  updated_at: item.updated_at,
});

const buildFallbackResult = (): BannersResult => {
  const raw = bannersFallback as {
    home_banners?: any[];
    side_banners?: any[];
  };

  return {
    mainBanners: (raw.side_banners || []).map((i) =>
      mapRawBanner('MAIN_BANNER', i),
    ),
    homeBanners: (raw.home_banners || []).map((i) =>
      mapRawBanner('HOME_BANNER', i),
    ),
  };
};

const isUsableResponse = (
  banners: { home_banners: Banner[]; side_banners: Banner[] } | undefined,
): boolean => {
  if (!banners) return false;

  return banners.home_banners.length > 0 || banners.side_banners.length > 0;
};

export const fetchBannersHandler: Handler<Params, Ports, Result> = async (
  ports = {
    apiFetchBanners: resolveAdapter('apiFetchBanners'),
  },
) => {
  if (cached && Date.now() - cachedAt < CACHE_TTL_MS) {
    return cached;
  }

  if (inflight) {
    const result = await inflight;

    if (result) return result;
  } else {
    inflight = (async () => {
      const [banners, bannersError] = await tryCatch(
        ports?.apiFetchBanners.handle(),
      );

      if (bannersError) {
        console.error('Error fetching banners:', bannersError);

        return null;
      }

      if (!isUsableResponse(banners)) return null;

      const result: BannersResult = {
        mainBanners: banners!.side_banners,
        homeBanners: banners!.home_banners,
      };

      cached = result;
      cachedAt = Date.now();

      return result;
    })();

    try {
      const result = await inflight;

      if (result) return result;
    } finally {
      inflight = null;
    }
  }

  if (cached) return cached;

  return buildFallbackResult();
};

type Params = undefined;

type Ports = {
  apiFetchBanners: ApiFetchBannersPort;
};

type Result = Promise<{ mainBanners: Banner[]; homeBanners: Banner[] }>;
